import React, { useState, useEffect } from 'react';
import logger from '@/lib/logger';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { X, Cookie, Settings } from 'lucide-react';
import { Link } from 'react-router-dom';

interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
}

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    essential: true, // Always true, cannot be disabled
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const consent = localStorage.getItem('cookie-consent');
    if (!consent) {
      setShowBanner(true);
    } else {
      const savedPreferences = JSON.parse(consent);
      setPreferences(savedPreferences);
    }
  }, []);

  const savePreferences = (prefs: CookiePreferences) => {
    localStorage.setItem(
      'cookie-consent',
      JSON.stringify({
        ...prefs,
        timestamp: Date.now(),
        version: '1.0',
      }),
    );
    setPreferences(prefs);
    setShowBanner(false);
    setShowPreferences(false);

    // Initialize analytics based on preferences
    if (prefs.analytics) {
      // Initialize Google Analytics or other analytics tools
      logger.info('[Cookies] Analytics cookies accepted');
    }

    if (prefs.marketing) {
      // Initialize marketing cookies
      logger.info('[Cookies] Marketing cookies accepted');
    }
  };

  const acceptAll = () => {
    savePreferences({
      essential: true,
      analytics: true,
      marketing: true,
    });
  };

  const acceptEssential = () => {
    savePreferences({
      essential: true,
      analytics: false,
      marketing: false,
    });
  };

  const handlePreferenceChange = (type: keyof CookiePreferences, checked: boolean) => {
    if (type === 'essential') return; // Essential cookies cannot be disabled

    setPreferences((prev) => ({
      ...prev,
      [type]: checked,
    }));
  };

  if (!showBanner) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end justify-center p-4">
      <Card className="w-full max-w-2xl bg-background border shadow-lg">
        <div className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-2">
              <Cookie className="h-5 w-5 text-primary" />
              <h3 className="text-lg font-semibold">Configuración de Cookies</h3>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowBanner(false)}
              className="h-6 w-6"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {!showPreferences ? (
            <>
              <p className="text-sm text-muted-foreground mb-6">
                Utilizamos cookies para mejorar su experiencia en nuestro sitio web, analizar el
                tráfico y personalizar el contenido. Puede elegir qué tipos de cookies acepta.
              </p>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button onClick={acceptAll} className="flex-1">
                  Aceptar Todas
                </Button>
                <Button variant="outline" onClick={acceptEssential} className="flex-1">
                  Solo Esenciales
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setShowPreferences(true)}
                  className="flex-1"
                >
                  <Settings className="h-4 w-4 mr-2" />
                  Personalizar
                </Button>
              </div>

              <div className="mt-4 text-xs text-muted-foreground">
                <Link to="/cookies" className="hover:underline">
                  Política de Cookies
                </Link>
                {' | '}
                <Link to="/privacy" className="hover:underline">
                  Política de Privacidad
                </Link>
              </div>
            </>
          ) : (
            <>
              <div className="space-y-4 mb-6">
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div>
                    <h4 className="font-medium">Cookies Esenciales</h4>
                    <p className="text-xs text-muted-foreground">
                      Necesarias para el funcionamiento básico del sitio
                    </p>
                  </div>
                  <Checkbox checked={preferences.essential} disabled className="opacity-50" />
                </div>

                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div>
                    <h4 className="font-medium">Cookies Analíticas</h4>
                    <p className="text-xs text-muted-foreground">
                      Nos ayudan a entender cómo usa nuestro sitio
                    </p>
                  </div>
                  <Checkbox
                    checked={preferences.analytics}
                    onCheckedChange={(checked) => handlePreferenceChange('analytics', checked as boolean)}
                  />
                </div>

                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div>
                    <h4 className="font-medium">Cookies de Marketing</h4>
                    <p className="text-xs text-muted-foreground">
                      Para mostrar anuncios relevantes y medir campañas
                    </p>
                  </div>
                  <Checkbox
                    checked={preferences.marketing}
                    onCheckedChange={(checked) => handlePreferenceChange('marketing', checked as boolean)}
                  />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button onClick={() => savePreferences(preferences)} className="flex-1">
                  Guardar Preferencias
                </Button>
                <Button variant="outline" onClick={() => setShowPreferences(false)} className="flex-1">
                  Volver
                </Button>
              </div>
            </>
          )}
        </div>
      </Card>
    </div>
  );
};

export default CookieConsent;
